import React, { useState, useEffect } from "react";
import Select from "react-select";
import "./SelectSpecialistModal.css";
import { getUsersSummary, getUserById, getAllCounterparties } from "../../../../api/api";
import { selectStyles } from "../../../../utils/selectStyles";

const SelectSpecialistModal = ({
  open,
  onClose,
  onSubmit,
  blockName,
  sectionName,
}) => {
  const [selectedType, setSelectedType] = useState("employee");
  const [selectedEmployeeType, setSelectedEmployeeType] = useState("fulltime");
  const [selectedLegalType, setSelectedLegalType] = useState("ip"); // подвид юр. лица: "ip" | "llp-no-vat" | "llp-with-vat"
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCounterparty, setSelectedCounterparty] = useState(null);
  const [rate, setRate] = useState("400 000");
  const [employees, setEmployees] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);

  // Функция для загрузки сотрудников
  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await getUsersSummary({
        isActive: true,
        pageSize: 1000,
      });

      const formattedEmployees = response.users.map((user) => ({
        id: user.id,
        value: user.id,
        name: `${user.surname || user.lastName || ""} ${
          user.name || user.firstName || ""
        } ${user.patronymic || user.middleName || ""}`.trim(),
        label: `${user.surname || user.lastName || ""} ${
          user.name || user.firstName || ""
        } ${user.patronymic || user.middleName || ""}`.trim(),
        fullName: `${user.surname || user.lastName || ""} ${
          user.name || user.firstName || ""
        } ${user.patronymic || user.middleName || ""}`.trim(),
        shortName: `${user.surname || user.lastName || ""} ${(
          user.name ||
          user.firstName ||
          ""
        ).charAt(0)}.`,
        userData: user,
      }));

      setEmployees(formattedEmployees);
    } catch (error) {
      console.error("Ошибка при загрузке сотрудников:", error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Загружаем сотрудников при выборе типа «Сотрудник»
  useEffect(() => {
    if (selectedType === "employee" && employees.length === 0) {
      loadEmployees();
    }
  }, [selectedType, employees.length]);

  // Загрузка контрагентов для субподрядчика (ФЛ на ГПХ / ИП / ТОО без НДС / ТОО с НДС)
  const loadCounterparties = async () => {
    try {
      setLoadingCounterparties(true);
      const list = await getAllCounterparties({ counterparty_side: "counterparty" });
      const arr = Array.isArray(list) ? list : [];

      const filterBySubcontractorType = (item) => {
        if (selectedType === "individual") {
          return item.type === "physical_entity" && item.legal_form === "Физическое лицо на ГПХ";
        }
        if (selectedType === "legal") {
          if (selectedLegalType === "ip") {
            return item.type === "physical_entity" && item.legal_form === "ИП";
          }
          if (selectedLegalType === "llp-no-vat") {
            return item.type === "legal_entity" && !item.is_nds_payer;
          }
          if (selectedLegalType === "llp-with-vat") {
            return item.type === "legal_entity" && item.is_nds_payer;
          }
        }
        return false;
      };

      const filtered = arr.filter(filterBySubcontractorType);
      const options = filtered.map((item) => {
        const label =
          item.full_name ||
          item.full_company_name ||
          item.short_company_name ||
          item.short_name ||
          `Контрагент #${item.id}`;
        return {
          value: item.id,
          label,
          data: item,
        };
      });
      setCounterparties(options);
    } catch (error) {
      console.error("Ошибка при загрузке контрагентов:", error);
      setCounterparties([]);
    } finally {
      setLoadingCounterparties(false);
    }
  };

  useEffect(() => {
    if (open && (selectedType === "individual" || selectedType === "legal")) {
      setSelectedCounterparty(null);
      loadCounterparties();
    }
  }, [open, selectedType, selectedLegalType]);

  // Сбрасываем состояние при открытии модального окна
  useEffect(() => {
    if (open) {
      setSelectedType("employee");
      setSelectedEmployeeType("fulltime");
      setSelectedLegalType("ip");
      setSelectedEmployee(null);
      setSelectedCounterparty(null);
      setRate("400 000");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let specialistData = {
      type: selectedType,
    };

    if (selectedType === "employee") {
      if (selectedEmployee) {
        const selectedEmployeeData = employees.find(
          (emp) => emp.id === selectedEmployee.value
        );

        if (selectedEmployeeData) {
          // Для штатных сотрудников загружаем полные данные
          if (selectedEmployeeType === "fulltime") {
            try {
              const userData = await getUserById(selectedEmployeeData.id);
              specialistData.name = selectedEmployeeData.shortName;
              specialistData.fullName = selectedEmployeeData.fullName;
              specialistData.employeeId = selectedEmployeeData.id;
              specialistData.userData = selectedEmployeeData.userData;
              specialistData.salary = userData.salary || 0;
            } catch (error) {
              console.error("Ошибка получения данных о зарплате:", error);
              specialistData.name = selectedEmployeeData.shortName;
              specialistData.fullName = selectedEmployeeData.fullName;
              specialistData.employeeId = selectedEmployeeData.id;
              specialistData.userData = selectedEmployeeData.userData;
              specialistData.salary = 0;
            }
          } else {
            // Для подрядчиков просто передаем данные сотрудника
            specialistData.name = selectedEmployeeData.shortName;
            specialistData.fullName = selectedEmployeeData.fullName;
            specialistData.employeeId = selectedEmployeeData.id;
            specialistData.userData = selectedEmployeeData.userData;
          }
        }
      }
      specialistData.employeeType = selectedEmployeeType;

      // Передаем rate только для подрядчиков в нерабочее время
      // Для штатных сотрудников и подрядчиков в рабочее время бэкенд сам подтянет оклад из базы
      if (selectedEmployeeType === "contract-nonworking") {
        specialistData.rate = `₸ ${rate}`;
      }
    } else if (selectedType === "individual" || selectedType === "legal") {
      if (selectedCounterparty) {
        const data = selectedCounterparty.data;
        specialistData.counterpartyId = selectedCounterparty.value;
        specialistData.name =
          data.short_name || data.short_company_name || data.full_name || data.full_company_name || "";
        specialistData.fullName =
          data.full_name || data.full_company_name || data.short_company_name || data.short_name || specialistData.name;
      }
      specialistData.legalType = selectedType === "legal" ? selectedLegalType : undefined;
      specialistData.rate = `₸ ${rate}`;
    }

    onSubmit && onSubmit(specialistData);
    onClose();
  };

  const handleClose = () => {
    setSelectedType("employee");
    setSelectedEmployeeType("fulltime");
    setSelectedLegalType("ip");
    setSelectedEmployee(null);
    setSelectedCounterparty(null);
    setRate("400 000");
    setEmployees([]);
    setCounterparties([]);
    setLoadingEmployees(false);
    setLoadingCounterparties(false);
    onClose();
  };

  if (!open) return null;

  // Формат блока для контекста: "Блок 1 9 эт." → "Блок 1 (9 эт.)"
  const formatBlockContext = (name) => {
    if (!name) return name;
    const m = name.match(/^(.+?)\s*(\d+\s*эт\.?)$/i);
    return m ? `${m[1].trim()} (${m[2].replace(/\s+/g, " ")})` : name;
  };

  const isEmployee = selectedType === "employee";
  const isSubcontractor = selectedType === "individual" || selectedType === "legal";
  const showRateField =
    (isEmployee && selectedEmployeeType === "contract-nonworking") ||
    isSubcontractor;

  return (
    <>
      <div className="select-specialist-backdrop" onClick={handleClose} />
      <div className="select-specialist-modal">
        <div className="select-specialist-header">
          <h2 className="select-specialist-title">Добавление исполнителя</h2>
          <div className="select-specialist-context">
            <span className="context-tab">{sectionName || "Раздел"}</span>
            <span className="context-divider">|</span>
            <span className="context-tab context-tab-active">
              {formatBlockContext(blockName) || "Блок"}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="select-specialist-content">
            {/* Тип исполнителя — по макету: Сотрудник и Субподрядчик */}
            <div className="form-section">
              <label className="form-label required">
                Выберите тип исполнителя*
              </label>

              {/* Сотрудник — раскрывается: Штатный сотрудник, Субподрядчик */}
              <div className="performer-type-option">
                <div
                  className={`performer-type-header ${
                    isEmployee ? "active" : "inactive"
                  }`}
                  onClick={() => setSelectedType("employee")}
                >
                  <span className="performer-type-icon performer-icon-person">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 20c0-3.5 3.13-6 7-6s7 2.5 7 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </span>
                  <span className="performer-type-label">Сотрудник</span>
                  <span className="performer-type-arrow">
                    {isEmployee ? "▲" : "▼"}
                  </span>
                </div>

                {isEmployee && (
                  <div className="performer-type-content">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="employeeType"
                        value="fulltime"
                        checked={true}
                        readOnly
                      />
                      <span className="radio-label">Штатный сотрудник</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Субподрядчик — раскрывается: ФЛ, ЮЛ, ИП, ТОО без/с НДС */}
              <div className="performer-type-option">
                <div
                  className={`performer-type-header ${
                    isSubcontractor ? "active" : "inactive"
                  }`}
                  onClick={() => {
                    if (!isSubcontractor) {
                      setSelectedType("individual");
                      setSelectedLegalType("ip");
                    }
                  }}
                >
                  <span className="performer-type-icon performer-icon-briefcase">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M4 8h16v12H4V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M4 8V6a2 2 0 012-2h12a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M8 8v4m8-4v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </span>
                  <span className="performer-type-label">Субподрядчик</span>
                  <span className="performer-type-arrow">
                    {isSubcontractor ? "▲" : "▼"}
                  </span>
                </div>

                {isSubcontractor && (
                  <div className="performer-type-content">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="subcontractorType"
                        value="individual"
                        checked={selectedType === "individual"}
                        onChange={() => setSelectedType("individual")}
                      />
                      <span className="radio-label">Физическое лицо</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="subcontractorType"
                        value="legal"
                        checked={selectedType === "legal"}
                        onChange={() => {
                          setSelectedType("legal");
                          setSelectedLegalType("ip");
                        }}
                      />
                      <span className="radio-label">Юридическое лицо</span>
                    </label>
                    {selectedType === "legal" && (
                      <div className="performer-type-content performer-type-content-nested">
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="legalSubType"
                            value="ip"
                            checked={selectedLegalType === "ip"}
                            onChange={() => setSelectedLegalType("ip")}
                          />
                          <span className="radio-label">ИП</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="legalSubType"
                            value="llp-no-vat"
                            checked={selectedLegalType === "llp-no-vat"}
                            onChange={() => setSelectedLegalType("llp-no-vat")}
                          />
                          <span className="radio-label">ТОО без НДС</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="legalSubType"
                            value="llp-with-vat"
                            checked={selectedLegalType === "llp-with-vat"}
                            onChange={() => setSelectedLegalType("llp-with-vat")}
                          />
                          <span className="radio-label">ТОО с НДС</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Выбор исполнителя / субподрядчика */}
            <div className="form-section">
              <label className="form-label required">
                {isEmployee
                  ? "Выберите исполнителя*"
                  : "Выберите субподрядчика из списка*"}
              </label>

              {isEmployee ? (
                <div className="select-wrapper select-with-icon">
                  {loadingEmployees ? (
                    <div className="loading-employees">
                      <span>Загрузка сотрудников...</span>
                    </div>
                  ) : (
                    <Select
                      value={selectedEmployee}
                      onChange={setSelectedEmployee}
                      options={employees}
                      placeholder="Иванов Иван Иванович"
                      isSearchable
                      isClearable
                      styles={selectStyles}
                      className="employee-select"
                      noOptionsMessage={() => "Нет доступных сотрудников"}
                      required
                    />
                  )}
                </div>
              ) : (
                <div className="select-wrapper select-with-icon">
                  {loadingCounterparties ? (
                    <div className="loading-employees">
                      <span>Загрузка контрагентов...</span>
                    </div>
                  ) : (
                    <Select
                      value={selectedCounterparty}
                      onChange={setSelectedCounterparty}
                      options={counterparties}
                      placeholder="Выберите из списка..."
                      isSearchable
                      isClearable
                      styles={selectStyles}
                      className="employee-select"
                      noOptionsMessage={() => "Нет контрагентов выбранного типа"}
                      required
                    />
                  )}
                </div>
              )}
            </div>

            {/* Введите ставку* — по макету: ₸ и иконка карандаша */}
            {showRateField && (
              <div className="form-section">
                <label className="form-label required">
                  Введите ставку*
                </label>
                <div className="input-wrapper rate-input-wrapper">
                  <span className="rate-currency">₸</span>
                  <input
                    type="text"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="400 000"
                    className="form-input form-input-rate"
                    required
                  />
                  <span className="rate-edit-icon" title="Изменить">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#2a5e38" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#2a5e38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="select-specialist-actions">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Отмена
            </button>
            <button type="submit" className="btn-add">
              <span className="add-icon">+</span>
              Добавить
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SelectSpecialistModal;
